<template>
  <div class="order-detail-page">
    <van-nav-bar
      title="订单详情"
      left-arrow
      @click-left="handleBack"
      fixed
      placeholder
    />
    <div class="content" v-if="orderDetail && !loading">
      <!-- 状态和取单号 -->
      <div class="status-section">
        <div class="status-title">{{ statusTitle }}</div>
        <div class="meal-code-wrapper">
          <span class="meal-code-label">取餐号</span>
          <span class="meal-code">{{ orderDetail.mealCode }}</span>
        </div>
        <van-button class="reorder-btn" round plain>再来一单</van-button>
      </div>

      <!-- 进度条 -->
      <!-- <div class="card progress-card">
        <van-steps :active="activeStep" active-color="#c7000b">
          <van-step>已下单</van-step>
          <van-step>制作中</van-step>
          <van-step>请取餐</van-step>
        </van-steps>
        <div class="queue-info" v-if="orderDetail.queueText">
          {{ orderDetail.queueText }}
        </div>
      </div> -->

      <!-- 店铺信息 -->
      <div class="shop-card">
        <div class="shop-header">
          <van-icon name="shop-o" size="16" />
          <span class="shop-name">{{ orderDetail.shopName }}</span>
        </div>
        <div class="shop-address">{{ orderDetail.shopAddress }}</div>
        <div class="shop-actions">
          <span class="action-btn">联系门店</span>
          <span class="action-btn">前往门店</span>
        </div>
      </div>

      <!-- 商品列表 -->
      <div class="goods-card">
        <div v-for="item in orderDetail.items" :key="item.id" class="goods-item">
          <van-image
            width="60"
            height="60"
            :src="item.image"
            class="goods-image"
          />
          <div class="goods-info">
            <div class="goods-name">{{ item.name }}</div>
            <div class="goods-sku">{{ item.sku }}</div>
          </div>
          <div class="goods-right">
            <div class="goods-price">¥{{ item.price }}</div>
            <div class="goods-quantity">x{{ item.quantity }}</div>
          </div>
        </div>
        
        <!-- 优惠信息 -->
        <van-cell 
          v-if="orderDetail.discounts && orderDetail.discounts.length > 0"
          title="优惠合计" 
          is-link 
          @click="showDiscountDetail = !showDiscountDetail"
        >
          <template #default>
            <span class="discount-amount">-¥{{ orderDetail.totalDiscount }}</span>
          </template>
        </van-cell>
        
        <!-- 订单统计 -->
        <div class="order-summary">
          共{{ orderDetail.items.length }}件商品，实付：<span class="total-price">¥{{ orderDetail.finalAmount }}</span>
        </div>
      </div>

      <!-- 订单信息 -->
      <div class="order-info-card">
        <div class="info-row">
          <span class="info-label">下单时间</span>
          <span class="info-value">{{ orderDetail.orderTime }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">订单编号</span>
          <span class="info-value">
            {{ orderDetail.orderNo }}
            <span class="copy-text" @click="copyOrderNo">复制</span>
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">取茶方式</span>
          <span class="info-value">{{ orderDetail.diningStyle }}</span>
        </div>
      </div>

      <!-- 底部logo -->
      <div class="footer-logo">
        <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiNkZGQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPuWPpOiMlzwvdGV4dD48L3N2Zz4=" alt="goodme" />
      </div>

    </div>
    <div class="content placeholder" v-else>
      <van-loading size="24px" vertical>
        <template v-if="orderDetail">
          正在获取取餐号...<br>
          <span style="font-size: 12px; color: #999;">{{ pollingRetries > 0 ? `已重试 ${pollingRetries} 次` : '' }}</span>
        </template>
        <template v-else>
          正在加载订单详情...
        </template>
      </van-loading>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request';
import { mapState } from 'vuex';

export default {
  name: 'OrderDetail',
  data() {
    return {
      orderNo: '',
      orderDetail: null,
      pollingTimer: null,
      pollingRetries: 0,
      maxPollingRetries: 20, // 最多轮询20次 (约1分钟)
      loading: true, // 添加loading状态
      showDiscountDetail: false, // 优惠详情展开状态
    };
  },
  computed: {
    ...mapState(['appConfig']),
    activeStep() {
      if (!this.orderDetail) return -1;
      const state = this.orderDetail.orderState;
      if (state >= 50) { // 待取餐或之后的状态
        return 2;
      }
      if (state === 40) { // 待备餐 (制作中)
        return 1;
      }
      if (state > 0 && state < 40) { // 已下单
        return 0;
      }
      return -1;
    },
    statusTitle() {
      if (!this.orderDetail) return '';
      const state = this.orderDetail.orderState;
      
      // 处理字符串状态
      if (typeof state === 'string') {
        const statusMap = {
          'WAIT_PAY': '待支付',
          'ORDER_PLACED': '已下单',
          'MAKING': '制作中',
          'WAIT_PICK': '待取餐',
          'FINISH': '已完成',
          'COMPLETED': '已完成',
          'CANCEL': '已取消',
          'CANCELLED': '已取消',
          'REFUNDED': '已退款'
        };
        return statusMap[state] || this.orderDetail.statusText || '已下单';
      }
      
      // 处理数字状态（兼容旧格式）
      if (state === 40) return '制作中';
      if (state >= 50 && state < 60) return '待取餐';
      if (state >= 60) return '已完成';
      return '已下单';
    },
  },
  created() {
    // 古茗使用 orderId
    this.orderNo = this.$route.query.orderId || this.$route.query.orderNo || '';
    if (this.orderNo) {
      this.getOrderDetail();
    } else {
      this.$toast('订单号不存在');
    }
  },
  beforeDestroy() {
    clearTimeout(this.pollingTimer);
  },
  methods: {
    handleBack() {
      // 优先使用浏览器返回
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        // 如果没有历史记录，跳转到卡密页面
        this.$router.push('/card-key');
      }
    },
    async getOrderDetail() {
      clearTimeout(this.pollingTimer);

      if (this.pollingRetries >= this.maxPollingRetries) {
        this.$toast('取餐号仍在生成中，请稍后刷新页面查看');
        this.pollingRetries = 0;
        this.loading = false; // 关闭loading，显示订单详情
        return;
      }

      try {
        // 调用古茗订单详情接口
        let response = await request.post('/gm/get-order-info', {
          orderId: this.orderNo
        });
        
        if (response) {
          response = response.data;
        }
        
        console.log('订单详情返回：', response);
        
        // 兼容两种返回格式：直接返回data或返回orderInfo包装
        let rawData = null;
        if (response && response.orderInfo && response.orderInfo.code === 0) {
          rawData = response.orderInfo.data;
        } else if (response && response.code === 0 && response.data) {
          rawData = response.data;
        }
        
        if (rawData) {

          // 格式化商品属性
          const formatProductAttrs = (attrList) => {
            if (!attrList || attrList.length === 0) return '';
            return attrList.map(attr => {
              const attrValues = attr.attrValueList.map(v => v.attrValueName).join(',');
              return attrValues;
            }).join(' / ');
          };
          
          // 解析商品列表
          const items = [];
          if (rawData.productInfo && rawData.productInfo.productList) {
            rawData.productInfo.productList.forEach(product => {
              items.push({
                id: product.productId,
                name: product.productName,
                image: product.productImageUri,
                sku: formatProductAttrs(product.productAttrList),
                quantity: product.quantity,
                price: product.priceInfo.actualPrice.amount.toFixed(2)
              });
            });
          }
          
          // 解析优惠信息
          const discounts = [];
          if (rawData.promotionData && rawData.promotionData.promotionList) {
            rawData.promotionData.promotionList.forEach(promo => {
              discounts.push({
                name: promo.promotionName,
                amount: promo.discountAmount.amount.toFixed(2)
              });
            });
          }
          
          // 获取取餐号
          let rankingNumber = rawData.mainOrderData?.posData?.rankingNumber || 
                             rawData.mainOrderData?.rankingNumber;
          
          // 判断是否有真实的取餐号
          const hasRealRankingNumber = !!rankingNumber;
          
          // 如果没有取餐号，显示"生成中..."
          if (!rankingNumber) {
            rankingNumber = '生成中...';
          }
          
          this.orderDetail = {
            statusText: rawData.orderStatusName || rawData.orderStatus,
            mealCode: rankingNumber,
            queueText: '',
            orderState: rawData.orderStatus,
            shopName: rawData.mainOrderData?.shopData?.shopName || '未知店铺',
            shopAddress: rawData.mainOrderData?.shopData?.address || '',
            shopPhone: rawData.mainOrderData?.shopData?.phone || '',
            items: items,
            discounts: discounts,
            totalDiscount: rawData.orderDiscountDetailVO?.discountAmountTotal?.amount?.toFixed(2) || '0.00',
            finalAmount: rawData.mainOrderData?.priceData?.totalPrice?.amount?.toFixed(2) || 
                        rawData.orderPayData?.payAmount?.amount?.toFixed(2) || '0.00',
            diningStyle: rawData.mainOrderData?.takeawayData?.deliveryTypeName || '自取',
            diningTime: '立即取餐',
            orderNo: rawData.orderId,
            orderTime: rawData.creator?.time || '',
            paymentMethod: rawData.orderPayData?.payChannelName || '在线支付',
            remarks: rawData.remark || '',
          };
          
          // 如果没有真实取餐号，继续轮询
          if (!hasRealRankingNumber) {
            this.pollingRetries++;
            console.log(`没有取餐号，3秒后重试 (第${this.pollingRetries}次)`);
            
            // 保持loading状态
            this.loading = true;
            
            // 3秒后重新获取订单详情
            this.pollingTimer = setTimeout(() => {
              this.getOrderDetail();
            }, 3000);
          } else {
            // 有取餐号了，停止轮询
            this.pollingRetries = 0;
            this.loading = false;
            console.log('已获取到取餐号:', rankingNumber);
          }

        } else {
          const errorMsg = response?.orderInfo?.msg || response?.message || '未知错误';
          this.$toast('获取订单详情失败: ' + errorMsg);
          this.loading = false;
        }
      } catch (error) {
        this.$toast('获取订单详情异常');
        console.error(error);
        this.loading = false;
      }
    },
    copyOrderNo() {
      if (!this.orderDetail || !this.orderDetail.orderNo) return;
      navigator.clipboard.writeText(this.orderDetail.orderNo).then(() => {
        this.$toast('复制成功');
      }).catch(err => {
        this.$toast('复制失败');
        console.error('Could not copy text: ', err);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.order-detail-page {
  background-color: #f5f5f5;
  min-height: 100vh;
  
  .content {
    padding-bottom: 60px;
  }
  
  .placeholder {
    padding-top: 100px;
    text-align: center;
  }
  
  // 状态区域
  .status-section {
    background: #fff;
    padding: 30px 16px 20px;
    text-align: center;
    margin-bottom: 10px;
    
    .status-title {
      font-size: 18px;
      font-weight: 500;
      color: #333;
      margin-bottom: 12px;
    }
    
    .meal-code-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-bottom: 20px;
      
      .meal-code-label {
        font-size: 14px;
        color: #666;
      }
      
      .meal-code {
        font-size: 28px;
        font-weight: bold;
        color: #333;
      }
    }
    
    .reorder-btn {
      border-color: #ff6034;
      color: #ff6034;
      font-size: 14px;
      padding: 0 32px;
      height: 36px;
    }
  }

  // 店铺卡片
  .shop-card {
    background: #fff;
    padding: 16px;
    margin-bottom: 10px;
    
    .shop-header {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 8px;
      
      .shop-name {
        font-size: 16px;
        font-weight: 500;
        color: #333;
      }
    }
    
    .shop-address {
      font-size: 13px;
      color: #666;
      margin-bottom: 12px;
      padding-left: 22px;
    }
    
    .shop-actions {
      display: flex;
      justify-content: flex-end;
      gap: 24px;
      
      .action-btn {
        font-size: 14px;
        color: #333;
        cursor: pointer;
      }
    }
  }
  
  // 商品卡片
  .goods-card {
    background: #fff;
    padding: 16px;
    margin-bottom: 10px;
    
    .goods-item {
      display: flex;
      align-items: flex-start;
      margin-bottom: 16px;
      
      &:last-of-type {
        margin-bottom: 12px;
      }
      
      .goods-image {
        flex-shrink: 0;
        margin-right: 12px;
        border-radius: 4px;
      }
      
      .goods-info {
        flex: 1;
        
        .goods-name {
          font-size: 15px;
          color: #333;
          margin-bottom: 6px;
        }
        
        .goods-sku {
          font-size: 12px;
          color: #999;
          line-height: 1.4;
        }
      }
      
      .goods-right {
        text-align: right;
        
        .goods-price {
          font-size: 15px;
          color: #333;
          margin-bottom: 4px;
        }
        
        .goods-quantity {
          font-size: 13px;
          color: #666;
        }
      }
    }
    
    .discount-amount {
      color: #ff6034;
      font-size: 14px;
    }
    
    .order-summary {
      text-align: right;
      font-size: 14px;
      color: #333;
      padding-top: 12px;
      border-top: 1px solid #f5f5f5;
      
      .total-price {
        color: #ff6034;
        font-weight: 500;
      }
    }
  }
  
  // 订单信息卡片
  .order-info-card {
    background: #fff;
    padding: 0 16px;
    margin-bottom: 10px;
    
    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 0;
      border-bottom: 1px solid #f5f5f5;
      
      &:last-child {
        border-bottom: none;
      }
      
      .info-label {
        font-size: 14px;
        color: #666;
      }
      
      .info-value {
        font-size: 14px;
        color: #333;
        max-width: 60%;
        text-align: right;
        
        .copy-text {
          color: #ff6034;
          margin-left: 8px;
          cursor: pointer;
        }
      }
    }
  }
  
  // 底部logo
  .footer-logo {
    text-align: center;
    padding: 30px 0;
    
    img {
      width: 80px;
      opacity: 0.3;
    }
  }
  
  // 自定义van-cell样式
  ::v-deep .van-cell {
    padding: 10px 0;
    font-size: 14px;
    
    &::after {
      border-bottom-color: #f5f5f5;
    }
  }
}
</style> 